import {sleep} from '@benchling/js-utils';

import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {ChatSessionQuery, ChatSessionQueryVariables} from 'llm/chat-library/__generated/ChatSessionQuery';
import {ChatFileUpload, UserContext} from 'llm/chat-library/ChatLibraryTypes';
import {CHAT_SESSION_QUERY} from 'llm/chat-library/useChatSessionEvents';
import * as Net from 'util/Net';

const POLL_TIMEOUT_MS = 1000;

export interface InitiateDeepResearchMessageResponse {
  chatId: string;
  statusUrl: string;
  userEventId?: string;
}

/**
 * Initiates a deep research message request and returns immediately with the chatId and statusUrl
 * This allows the chatId to be persisted before the potentially lengthy polling process begins
 */
export async function initiateDeepResearchMessage(
  chatId: string | undefined,
  message: string,
  publicDataEnabled: boolean,
  fileUploads: Array<ChatFileUpload>
): Promise<InitiateDeepResearchMessageResponse> {
  const initialResponse = await Net.postJSONPromise({
    url: '/1/api/llm/deep-research',
    data: {chatId, message, publicDataEnabled, fileUploads},
  });
  return {
    chatId: initialResponse.chatId,
    statusUrl: initialResponse.status_url,
  };
}

/**
 * Polls the deep research message task until completion and returns the result.
 */
export async function pollDeepResearchMessage(
  chatId: string,
  abortSignal: AbortSignal | undefined,
  onProgress: (progressData: ChatSessionQuery) => boolean
): Promise<void> {
  while (true) {
    if (abortSignal?.aborted) {
      throw new DOMException('Deep research polling aborted', 'AbortError');
    }
    const chatProgress = await doQuerySafe<ChatSessionQuery, ChatSessionQueryVariables>(
      {query: CHAT_SESSION_QUERY},
      {
        variables: {
          chatSessionId: chatId,
          // Data used by onProgress callback.
          disableAllThreads: false,
        },
        fetchPolicy: 'network-only',
      }
    );
    const shouldContinuePolling = onProgress(chatProgress);
    if (!shouldContinuePolling) {
      return;
    }
    await sleep(POLL_TIMEOUT_MS);
  }
}

export async function initiateAskMessage(
  chatId: string,
  message: string,
  context?: UserContext,
  fileUploads?: Array<ChatFileUpload>
): Promise<InitiateDeepResearchMessageResponse> {
  const initialResponse = await Net.postJSONPromise({
    url: '/1/api/chats/ask',
    data: {chatId, message, context, fileUploads},
  });
  return {
    chatId: initialResponse.chatId,
    statusUrl: initialResponse.status_url,
    userEventId: initialResponse.userEventId,
  };
}

export async function createChatSession(): Promise<string> {
  const response = await Net.postJSONPromise({
    url: '/1/api/chats',
    data: {type: 'DEEP_RESEARCH'},
  });
  return response.id;
}
